
import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import CommonUtils from "../../../utils/Common_Utils";
import { LocalStorage } from "../../../utils/Utils";
import assesClient from "../../../features/recommendation/textRecommendation/data/api/AssesNameClient";
import { useApi } from "../../../service/ApiContext";
import {
    setActiveTab,
    setSelectedItem,
} from "../data/redux/ProgramAdvanceFilterRedux";

interface AssessmentItem {
    candidate_id: string;
    assessment_id: string;
    assessment_name: string;
    timestamp: string;
}

interface AssessmentResponse {
    assessment_names: AssessmentItem[];
}

interface AdvancedFilterProps {
    onView: (
        candidateId: string | null,
        assessmentId: string | null
    ) => void;
    selectedItems: AssessmentItem | null;
    setSelectedItems: (item: AssessmentItem) => void;
}

const IndustryAssessmentResultDropdown: React.FC<AdvancedFilterProps> = ({
    onView,
    setSelectedItems,
}) => {
    const dispatch = useDispatch();
    const api = useApi();

    const [programs, setPrograms] = useState<AssessmentItem[]>([]);
    const [industries, setIndustries] = useState<AssessmentItem[]>([]);

    const [activeTab, setActiveTabLocal] = useState<
        "industry" | "program"
    >("industry");

    const [selectedAssessment, setSelectedAssessment] =
        useState<AssessmentItem | null>(null);

    const [loading, setLoading] = useState(true);

    const [domainOpen, setDomainOpen] = useState(false);
    const [assessmentOpen, setAssessmentOpen] = useState(false);
    const [hoveredOption, setHoveredOption] = useState("");

    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fetch assessments API
    const fetchAssessmentsApi = async (
        domain: string
    ): Promise<AssessmentItem[]> => {
        const param = {
            offset_time: CommonUtils.formatLocalTime(),
            domain_name: domain,
        };

        api.setAuthToken(LocalStorage.getLocalData("access_token"));

        const response = await api.get<AssessmentResponse>(
            assesClient.assesUrl,
            param
        );

        return response.data?.assessment_names || [];
    };

    // Fetch Industry and Program assessments
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const [industryData, programData] = await Promise.all([
                    fetchAssessmentsApi("Industry"),
                    fetchAssessmentsApi("Program"),
                ]);

                setIndustries(industryData);
                setPrograms(programData);

                const defaultTab =
                    industryData.length > 0 ? "industry" : "program";

                const defaultList =
                    defaultTab === "industry"
                        ? industryData
                        : programData;

                setActiveTabLocal(defaultTab);
                dispatch(setActiveTab(defaultTab));

                if (defaultList.length > 0) {
                    const firstItem = defaultList[0];

                    setSelectedAssessment(firstItem);
                    setSelectedItems(firstItem);
                    dispatch(setSelectedItem(firstItem));

                    onView(
                        firstItem.candidate_id,
                        firstItem.assessment_id
                    );
                }
            } catch (error) {
                console.error("Error fetching assessments:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setDomainOpen(false);
                setAssessmentOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    const assessmentList =
        activeTab === "industry" ? industries : programs;

    // Domain change
    const handleTypeChange = (
        value: "industry" | "program"
    ) => {
        setActiveTabLocal(value);
        dispatch(setActiveTab(value));

        setDomainOpen(false);
        setAssessmentOpen(false);

        const list = value === "industry" ? industries : programs;
        const firstItem = list[0] || null;

        setSelectedAssessment(firstItem);

        if (firstItem) {
            setSelectedItems(firstItem);
            dispatch(setSelectedItem(firstItem));

            onView(
                firstItem.candidate_id,
                firstItem.assessment_id
            );
        } else {
            dispatch(setSelectedItem(null));
        }
    };

    // Assessment change
    const handleAssessmentChange = (assessment: AssessmentItem) => {
        setSelectedAssessment(assessment);
        setSelectedItems(assessment);
        dispatch(setSelectedItem(assessment));

        setAssessmentOpen(false);

        onView(
            assessment.candidate_id,
            assessment.assessment_id
        );
    };

    // Main wrapper
    const wrapperStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: "24px",
        flexWrap: "wrap",
    };

    // Label and dropdown alignment
    const fieldGroupStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: "12px",
    };

    // Label
    const labelStyle: React.CSSProperties = {
        minWidth: "25px",
        fontFamily: "Mazzard",
        fontSize: "14px",
        fontWeight: 600,
        color: "#334155",
        whiteSpace: "nowrap",
    };

    // Dropdown container
    const dropdownContainerStyle: React.CSSProperties = {
        position: "relative",
        minWidth: "190px",
    };

    // Dropdown trigger button
    const dropdownButtonStyle: React.CSSProperties = {
        width: "100%",
        height: "40px",
        padding: "0 14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        border: "1px solid #919293ff",
        borderRadius: "9px",
        backgroundColor: "#FFFFFF",
        color: "#334155",
        fontFamily: "Mazzard",
        fontSize: "14px",
        fontWeight: 500,
        outline: "none",
        cursor: "pointer",
        boxShadow: "0 2px 6px rgba(59, 130, 246, 0.08)",
        transition: "all 0.2s ease",
        textAlign: "left",
    };

    // Dropdown menu
    const menuStyle: React.CSSProperties = {
        position: "absolute",
        top: "calc(100% + 7px)",
        left: 0,
        width: "100%",
        minWidth: "100%",
        maxHeight: "230px",
        overflowY: "auto",
        padding: "5px",
        backgroundColor: "#FFFFFF",
        border: "1px solid #DBEAFE",
        borderRadius: "10px",
        boxShadow: "0 8px 24px rgba(30, 64, 175, 0.13)",
        zIndex: 1000,
    };

    // Individual menu option
    const getOptionStyle = (
        isSelected: boolean,
        isHovered: boolean
    ): React.CSSProperties => ({
        width: "100%",
        padding: "10px 12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: "none",
        borderRadius: "7px",
        backgroundColor: isSelected
            ? "#DBEAFE"
            : isHovered
                ? "#EFF6FF"
                : "#FFFFFF",
        color: isSelected ? "#031e38ff" : "#334155",
        fontFamily: "Mazzard",
        fontSize: "14px",
        fontWeight: isSelected ? 600 : 500,
        cursor: "pointer",
        textAlign: "left",
        transition: "all 0.15s ease",
    });

    const arrowStyle: React.CSSProperties = {
        fontSize: "12px",
        color: "#64748B",
        transition: "transform 0.2s ease",
        display: "inline-block",
    };

    return (
        <div ref={dropdownRef} style={wrapperStyle}>

            {/* Domain Dropdown */}
            <div style={fieldGroupStyle}>
                <label style={labelStyle}>Select your Domain</label>

                <div style={dropdownContainerStyle}>
                    <button
                        type="button"
                        disabled={loading}
                        onClick={() => {
                            setDomainOpen(!domainOpen);
                            setAssessmentOpen(false);
                        }}
                        style={{
                            ...dropdownButtonStyle,
                            opacity: loading ? 0.6 : 1,
                        }}
                    >
                        <span>
                            {activeTab === "industry"
                                ? "Industry"
                                : "Program"}
                        </span>

                        <span
                            style={{
                                ...arrowStyle,
                                transform: domainOpen
                                    ? "rotate(180deg)"
                                    : "rotate(0deg)",
                            }}
                        >
                            ▼
                        </span>
                    </button>

                    {domainOpen && (
                        <div style={menuStyle}>
                            {/* {industries.length > 0 && ( */}
                            <button
                                type="button"
                                style={getOptionStyle(
                                    activeTab === "industry",
                                    hoveredOption === "industry"
                                )}
                                onMouseEnter={() =>
                                    setHoveredOption("industry")
                                }
                                onMouseLeave={() =>
                                    setHoveredOption("")
                                }
                                onClick={() =>
                                    handleTypeChange("industry")
                                }
                            >
                                Industry
                                {activeTab === "industry" && (
                                    <span>✓</span>
                                )}
                            </button>
                            {/* )} */}

                            {/* {programs.length > 0 && ( */}
                            <button
                                type="button"
                                style={getOptionStyle(
                                    activeTab === "program",
                                    hoveredOption === "program"
                                )}
                                onMouseEnter={() =>
                                    setHoveredOption("program")
                                }
                                onMouseLeave={() =>
                                    setHoveredOption("")
                                }
                                onClick={() =>
                                    handleTypeChange("program")
                                }
                            >
                                Program
                                {activeTab === "program" && (
                                    <span>✓</span>
                                )}
                            </button>
                            {/* )} */}
                        </div>
                    )}
                </div>
            </div>

            {/* Assessment Dropdown */}
            <div style={fieldGroupStyle}>
                <label style={labelStyle}>Select your Assessment</label>

                <div
                    style={{
                        ...dropdownContainerStyle,
                        minWidth: "230px",
                    }}
                >
                    <button
                        type="button"
                        disabled={loading || assessmentList.length === 0}
                        onClick={() => {
                            setAssessmentOpen(!assessmentOpen);
                            setDomainOpen(false);
                        }}
                        style={{
                            ...dropdownButtonStyle,
                            opacity:
                                loading || assessmentList.length === 0
                                    ? 0.6
                                    : 1,
                        }}
                    >
                        <span
                            style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {loading
                                ? "Loading..."
                                : selectedAssessment?.assessment_name ||
                                "No assessments available"}
                        </span>

                        <span
                            style={{
                                ...arrowStyle,
                                flexShrink: 0,
                                transform: assessmentOpen
                                    ? "rotate(180deg)"
                                    : "rotate(0deg)",
                            }}
                        >
                            ▼
                        </span>
                    </button>

                    {assessmentOpen && (
                        <div style={menuStyle}>
                            {assessmentList.map((item) => {
                                const isSelected =
                                    selectedAssessment?.assessment_id ===
                                    item.assessment_id;

                                return (
                                    <button
                                        key={item.assessment_id}
                                        type="button"
                                        style={getOptionStyle(
                                            isSelected,
                                            hoveredOption ===
                                            item.assessment_id
                                        )}
                                        onMouseEnter={() =>
                                            setHoveredOption(
                                                item.assessment_id
                                            )
                                        }
                                        onMouseLeave={() =>
                                            setHoveredOption("")
                                        }
                                        onClick={() =>
                                            handleAssessmentChange(item)
                                        }
                                    >
                                        <span
                                            style={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {item.assessment_name}
                                        </span>

                                        {isSelected && (
                                            <span
                                                style={{
                                                    marginLeft: "8px",
                                                    color: "#2563EB",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                ✓
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default IndustryAssessmentResultDropdown;
